import { useEffect, useState, useCallback } from "react";
import { Eye, Pencil, Send, ShoppingBag, Clock, Loader2, FileDown, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { api, fmtXof, fmtDate, apiError } from "../../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_FR = {
  nouveau: "Nouveau", en_cours: "En cours d'étude", envoye: "Envoyé au client",
  accepte: "Accepté", refuse: "Refusé", expire: "Expiré",
};
const STATUS_STYLE = {
  nouveau: "border-orange-300 bg-orange-50 text-orange-700",
  en_cours: "border-sky-300 bg-sky-50 text-ciel",
  envoye: "border-emerald-300 bg-emerald-50 text-emerald-700",
  accepte: "border-emerald-500 bg-emerald-100 text-emerald-800",
  refuse: "border-red-300 bg-red-50 text-red-600",
  expire: "border-slate-300 bg-slate-100 text-slate-500",
};

function StatCard({ label, value, sub, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{label}</p>
      <p className="mt-1.5 font-display text-xl font-bold text-ciel">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

export function DevisManager() {
  const [devis, setDevis] = useState([]);
  const [stats, setStats] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [editObs, setEditObs] = useState("");
  const [editAgentName, setEditAgentName] = useState("");
  const [editAgentSig, setEditAgentSig] = useState("");
  const [busy, setBusy] = useState(false);
  const [relancing, setRelancing] = useState(false);
  const sendAcompteReminders = async () => {
    setRelancing(true);
    try {
      const { data } = await api.post("/admin/devis/acompte-reminders");
      toast.success(data.emails_sent > 0 ? `${data.emails_sent} relance(s) d'acompte envoyée(s).` : "Aucun devis à relancer (signé +48h sans acompte).");
    } catch (e) { toast.error(apiError(e)); } finally { setRelancing(false); }
  };
  const markExpired = async () => {
    try {
      const { data } = await api.post("/admin/devis/mark-expired");
      toast.success(data.expired > 0 ? `${data.expired} devis marqué(s) expiré(s).` : "Aucun devis expiré à signaler.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const sendExpiryWarnings = async () => {
    try {
      const { data } = await api.post("/admin/devis/expiry-warnings");
      toast.success(data.emails_sent > 0 ? `${data.emails_sent} relance(s) J-3 envoyée(s).` : "Aucun devis proche de l'expiration.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const load = useCallback(async () => {
    try {
      const [d, s] = await Promise.all([api.get("/admin/devis"), api.get("/admin/devis/stats")]);
      setDevis(d.data);
      setStats(s.data);
    } catch (e) { toast.error(apiError(e, "Chargement impossible")); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (d, status) => {
    try {
      await api.patch(`/admin/devis/${d.devis_id}`, { status });
      toast.success(`Statut : ${STATUS_FR[status]}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const sendToClient = async (d) => {
    setBusy(true);
    try {
      await api.post(`/admin/devis/${d.devis_id}/send`);
      toast.success(`Devis ${d.number} envoyé à ${d.email}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const convert = async (d) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/devis/${d.devis_id}/convert`);
      toast.success(`Devis converti en commande ${data.order_id}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const openEdit = (d) => {
    setEditing(d);
    setEditItems(d.items.map((it) => ({ ...it })));
    setEditObs(d.observations || "");
    setEditAgentName(d.agent_name || "");
    setEditAgentSig(d.agent_signature || "");
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/devis/${editing.devis_id}`, {
        items: editItems, observations: editObs,
        agent_name: editAgentName, agent_signature: editAgentSig,
      });
      toast.success("Devis mis à jour");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const downloadPdf = async (d) => {
    try {
      const res = d.sign_token
        ? await api.get(`/devis/sign/${d.sign_token}/pdf`, { responseType: "blob" })
        : await api.post("/devis/pdf", {
            items: d.items, civility: d.civility, name: d.name, function: d.function,
            institution: d.institution, country: d.country, city: d.city, email: d.email, phone: d.phone,
          }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${d.number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible"); }
  };

  return (
    <div data-testid="devis-manager">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-display text-base font-semibold text-ciel">Gestion des devis</h3>
        <div className="flex gap-2">
          <Button data-testid="devis-mark-expired" onClick={markExpired} variant="outline" className="rounded-full border-slate-300 text-xs text-slate-600 hover:bg-slate-50"><Clock size={13} className="mr-1.5" /> Marquer les expirés (30j)</Button>
          <Button data-testid="devis-expiry-warnings" onClick={sendExpiryWarnings} variant="outline" className="rounded-full border-orange-300 text-xs text-orange-700 hover:bg-orange-50"><Clock size={13} className="mr-1.5" /> Relance J-3</Button>
          <Button data-testid="devis-relance-acompte" onClick={sendAcompteReminders} disabled={relancing} variant="outline" className="rounded-full border-amber-300 text-xs text-amber-700 hover:bg-amber-50">
            {relancing ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Send size={13} className="mr-1.5" />} Relancer les acomptes (48h)
          </Button>
        </div>
      </div>
      {/* STATISTIQUES */}
      {stats && (
        <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5" data-testid="devis-stats">
          <StatCard label="Devis reçus ce mois" value={stats.month_count} testid="devis-stat-month" />
          <StatCard label="Conversion devis → commande" value={`${stats.conversion} %`} testid="devis-stat-conversion" />
          <StatCard label="Montant en cours" value={fmtXof(stats.in_progress_amount)} testid="devis-stat-amount" />
          <StatCard label="Pays les plus demandeurs" value={stats.top_countries[0]?.[0] || "—"}
            sub={stats.top_countries.map(([c, n]) => `${c} (${n})`).join(" · ")} testid="devis-stat-countries" />
          <StatCard label="Service le plus demandé" value={stats.top_service ? stats.top_service.slice(0, 22) : "—"}
            sub={stats.top_service.length > 22 ? stats.top_service : ""} testid="devis-stat-service" />
        </div>
      )}

      {/* TABLEAU */}
      {devis.length === 0 ? (
        <p className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500" data-testid="no-devis">
          Aucune demande de devis pour le moment.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="devis-table">
          <table className="w-full min-w-[1000px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left font-mono text-[11px] uppercase tracking-wider text-slate-400">
                <th className="p-4">N° devis</th><th className="p-4">Client</th><th className="p-4">Produits demandés</th>
                <th className="p-4 text-right">Total TTC</th><th className="p-4">Date</th><th className="p-4">Statut</th><th className="p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {devis.map((d) => (
                <tr key={d.devis_id} className="border-b border-slate-100 align-middle" data-testid={`devis-row-${d.devis_id}`}>
                  <td className="p-4 font-mono text-xs font-semibold text-navy">{d.number}</td>
                  <td className="p-4">
                    <p className="font-medium text-slate-800">{d.civility} {d.name}</p>
                    <p className="text-xs text-slate-400">{d.institution || "—"} {d.country ? `· ${d.country}` : ""}</p>
                  </td>
                  <td className="max-w-[220px] p-4 text-xs text-slate-600">
                    {d.items.map((it) => it.title).join(", ").slice(0, 80)}{d.items.map((it) => it.title).join(", ").length > 80 ? "…" : ""}
                  </td>
                  <td className="p-4 text-right font-mono text-xs font-semibold text-navy">{fmtXof(d.totals?.ttc_afrique || 0)}</td>
                  <td className="p-4 font-mono text-xs text-slate-500">{fmtDate(d.created_at)}</td>
                  <td className="p-4">
                    <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold ${STATUS_STYLE[d.status]}`}
                      data-testid={`devis-status-${d.devis_id}`}>{STATUS_FR[d.status]}</span>
                  </td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-1.5">
                      <button onClick={() => setDetail(d)} data-testid={`devis-view-${d.devis_id}`} title="Voir le détail"
                        className="rounded-lg border border-slate-200 p-2 text-slate-500 transition-colors duration-200 hover:border-ciel hover:text-ciel"><Eye size={13} /></button>
                      <button onClick={() => openEdit(d)} data-testid={`devis-edit-${d.devis_id}`} title="Modifier le devis"
                        className="rounded-lg border border-slate-200 p-2 text-slate-500 transition-colors duration-200 hover:border-ciel hover:text-ciel"><Pencil size={13} /></button>
                      <button onClick={() => downloadPdf(d)} data-testid={`devis-pdf-${d.devis_id}`} title="Télécharger le PDF"
                        className="rounded-lg border border-slate-200 p-2 text-slate-500 transition-colors duration-200 hover:border-ciel hover:text-ciel"><FileDown size={13} /></button>
                      <button onClick={() => sendToClient(d)} disabled={busy} data-testid={`devis-send-${d.devis_id}`} title="Envoyer au client (email)"
                        className="rounded-lg border border-slate-200 p-2 text-slate-500 transition-colors duration-200 hover:border-emerald-400 hover:text-emerald-600"><Send size={13} /></button>
                      {!d.order_id && (
                        <button onClick={() => convert(d)} disabled={busy} data-testid={`devis-convert-${d.devis_id}`} title="Convertir en commande"
                          className="rounded-lg border border-slate-200 p-2 text-slate-500 transition-colors duration-200 hover:border-navy hover:text-navy"><ShoppingBag size={13} /></button>
                      )}
                      {d.status !== "expire" && !d.order_id && (
                        <button onClick={() => setStatus(d, "expire")} data-testid={`devis-expire-${d.devis_id}`} title="Marquer comme expiré"
                          className="rounded-lg border border-slate-200 p-2 text-slate-400 transition-colors duration-200 hover:border-slate-400 hover:text-slate-600"><Clock size={13} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* DÉTAIL */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="devis-detail-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Devis {detail?.number}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="rounded-lg bg-slate-50 p-4">
                <p className="font-semibold text-navy">{detail.civility} {detail.name}</p>
                <p className="text-xs text-slate-500">
                  {[detail.function, detail.institution, detail.city, detail.country].filter(Boolean).join(" · ")}
                </p>
                <p className="mt-1 text-xs text-slate-500">{detail.email} · {detail.phone}</p>
                <p className="mt-1 text-xs text-slate-500">Paiement envisagé : {detail.payment_method || "—"}</p>
                {detail.rdv_visio && <p className="mt-1 text-xs font-semibold text-ciel">RDV visio souhaité — dispos : {detail.dispos || "à convenir"}</p>}
              </div>
              {detail.items.map((it, i) => (
                <div key={i} className="rounded-lg border border-slate-100 p-4">
                  <p className="font-mono text-[11px] text-slate-400">{detail.number}-{String(i + 1).padStart(2, "0")}</p>
                  <p className="font-medium text-navy">{it.title}</p>
                  <p className="text-xs text-slate-500">{it.category}{it.subtype ? ` / ${it.subtype}` : ""} · Délai : {it.delay || "à définir"}</p>
                  <p className="mt-1 font-mono text-xs font-semibold text-navy">
                    {fmtXof(it.price)} × {it.qty || 1}{it.maintenance ? " + maintenance 10 %" : ""}
                  </p>
                  {it.conditions && <p className="mt-1 text-xs italic text-slate-500">{it.conditions}</p>}
                </div>
              ))}
              <div className="rounded-lg bg-navy p-4 text-white">
                <p className="text-xs text-slate-300">Total HT : {fmtXof(detail.totals?.total_ht || 0)} · TVA 18 % : {fmtXof(detail.totals?.tva_afrique || 0)}</p>
                <p className="font-display text-lg font-bold">Total TTC : {fmtXof(detail.totals?.ttc_afrique || 0)}</p>
              </div>
              {detail.observations && <p className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">{detail.observations}</p>}
              {(detail.agent_name || detail.agent_signature) && (
                <div className="flex justify-end" data-testid="devis-detail-agent">
                  <div className="w-56 text-right">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Le commercial</p>
                    {detail.agent_name && <p className="mt-0.5 text-sm font-medium text-navy">{detail.agent_name}</p>}
                    {detail.agent_signature && <p className="mt-0.5 text-lg italic text-ciel" style={{ fontFamily: "'Segoe Script','Brush Script MT',cursive" }}>{detail.agent_signature}</p>}
                    <div className="mt-1 border-t border-slate-300" />
                    <p className="mt-0.5 text-[10px] text-slate-400">Signature du commercial</p>
                  </div>
                </div>
              )}
              {detail.signed_at && (
                <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs font-semibold text-emerald-700" data-testid="devis-signed-info">
                  Signé par {detail.signed_by} le {fmtDate(detail.signed_at)}
                </p>
              )}
              {detail.acompte?.paid_at && (
                <p className="rounded-lg border border-emerald-300 bg-emerald-100 p-3 text-xs font-semibold text-emerald-800" data-testid="devis-acompte-info">
                  Acompte 30 % payé : {fmtXof(detail.acompte.amount)} le {fmtDate(detail.acompte.paid_at)} ({detail.acompte.method})
                </p>
              )}
              {detail.delivered_at && (
                <p className="rounded-lg border border-sky-300 bg-sky-50 p-3 text-xs font-semibold text-ciel" data-testid="devis-delivered-info">
                  Prestation livrée le {fmtDate(detail.delivered_at)}
                </p>
              )}
              {detail.solde?.paid_at && (
                <p className="rounded-lg border border-emerald-400 bg-emerald-100 p-3 text-xs font-semibold text-emerald-800" data-testid="devis-solde-info">
                  Solde 70 % payé : {fmtXof(detail.solde.amount)} le {fmtDate(detail.solde.paid_at)} ({detail.solde.method}) — Devis soldé
                </p>
              )}
              {detail.solde?.paid_at && detail.sign_token && (
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/devis/sign/${detail.sign_token}/facture`}
                  target="_blank" rel="noreferrer" data-testid="devis-invoice-btn"
                  className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-800">
                  <FileDown size={13} /> Télécharger la facture acquittée (PDF)
                </a>
              )}
              {detail.status === "accepte" && !detail.delivered_at && (
                <Button size="sm" data-testid="devis-deliver-btn" disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await api.post(`/admin/devis/${detail.devis_id}/deliver`);
                      toast.success("Prestation marquée livrée — le client est notifié pour le solde");
                      setDetail(null);
                      load();
                    } catch (e) { toast.error(apiError(e)); }
                    finally { setBusy(false); }
                  }}
                  className="rounded-full bg-ciel text-white hover:bg-sky-700">
                  <PackageCheck size={14} className="mr-1.5" /> Marquer la prestation comme livrée
                </Button>
              )}
              <div className="flex flex-wrap gap-2">
                {(["nouveau", "en_cours", "envoye", "accepte", "refuse", "expire"]).map((s) => (
                  <Button key={s} size="sm" variant={detail.status === s ? "default" : "outline"}
                    data-testid={`devis-detail-status-${s}`}
                    onClick={async () => { await setStatus(detail, s); setDetail(null); }}
                    className="rounded-full text-xs">{STATUS_FR[s]}</Button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* MODIFICATION */}
      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="devis-edit-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Modifier le devis {editing?.number}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {editItems.map((it, i) => (
              <div key={i} className="rounded-lg border border-slate-100 p-4">
                <p className="text-sm font-medium text-navy">{it.title}</p>
                <div className="mt-3 grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-slate-500">Prix HT (FCFA)</label>
                    <Input type="number" min={0} data-testid={`devis-edit-price-${i}`} value={it.price}
                      onChange={(e) => setEditItems(editItems.map((x, j) => j === i ? { ...x, price: parseInt(e.target.value) || 0 } : x))} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Délai</label>
                    <Input data-testid={`devis-edit-delay-${i}`} value={it.delay} placeholder="Ex : 6 semaines"
                      onChange={(e) => setEditItems(editItems.map((x, j) => j === i ? { ...x, delay: e.target.value } : x))} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Conditions</label>
                    <Input data-testid={`devis-edit-cond-${i}`} value={it.conditions}
                      onChange={(e) => setEditItems(editItems.map((x, j) => j === i ? { ...x, conditions: e.target.value } : x))} />
                  </div>
                </div>
              </div>
            ))}
            <div>
              <label className="text-xs text-slate-500">Observations (visibles par le client)</label>
              <textarea rows={3} data-testid="devis-edit-obs" value={editObs} onChange={(e) => setEditObs(e.target.value)}
                className="mt-1.5 w-full rounded-md border border-slate-300 p-3 text-sm outline-none focus:ring-2 focus:ring-ciel" />
            </div>
            <div className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="col-span-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Le commercial (bas de devis)</div>
              <div>
                <label className="text-xs text-slate-500">Nom du commercial</label>
                <input data-testid="devis-edit-agent-name" value={editAgentName} onChange={(e) => setEditAgentName(e.target.value)}
                  placeholder="Ex : Awa Koné" className="mt-1.5 w-full rounded-md border border-slate-300 p-2.5 text-sm outline-none focus:ring-2 focus:ring-ciel" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Signature</label>
                <input data-testid="devis-edit-agent-signature" value={editAgentSig} onChange={(e) => setEditAgentSig(e.target.value)}
                  placeholder="Signature (nom manuscrit)" style={{ fontFamily: "'Segoe Script','Brush Script MT',cursive" }}
                  className="mt-1.5 w-full rounded-md border border-slate-300 p-2.5 text-base italic text-ciel outline-none focus:ring-2 focus:ring-ciel" />
              </div>
            </div>
            <Button data-testid="devis-edit-save" onClick={saveEdit} disabled={busy}
              className="w-full rounded-full bg-navy py-5 text-sm font-semibold text-white hover:bg-navy-light">
              {busy ? <Loader2 className="animate-spin" size={15} /> : "Enregistrer les modifications"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
